/** A run of the row's text, flagged with whether it is part of what the user typed. */
export interface HighlightSegment {
  text: string;
  match: boolean;
}

/**
 * Split `text` into matched / unmatched runs using the offsets Atlas returned.
 *
 * The offsets come from the search provider rather than from re-searching the string here, and that is
 * the whole point: a prediction can match on a fold, a transliteration or a diacritic-stripped form
 * ("tel aviv" matching "Tel Aviv-Yafo", "koln" matching "Köln"), and `indexOf` finds none of those. It
 * finds the wrong thing instead, which is worse than finding nothing.
 *
 * Ranges arrive unsorted and, in principle, overlapping or out of bounds, so they are normalized here:
 * sorted, clamped to the string, and merged when they touch. Everything a renderer needs is then a flat
 * walk, and a malformed range degrades to plain text rather than to a scrambled row.
 */
export function highlightMatches(text: string, matched: { offset: number; length: number }[] | undefined): HighlightSegment[] {
  if (!text) return [];
  const ranges = (matched ?? [])
    .map(({ offset, length }) => ({ start: Math.max(0, offset), end: Math.min(text.length, offset + length) }))
    .filter((r) => r.end > r.start)
    .sort((a, b) => a.start - b.start);

  if (ranges.length === 0) return [{ text, match: false }];

  const merged: { start: number; end: number }[] = [];
  for (const range of ranges) {
    const last = merged[merged.length - 1];
    // `>=` merges touching ranges too, not just overlapping ones — two adjacent <b> runs render the same
    // as one but give the DOM a seam for no reason.
    if (last && range.start <= last.end) {
      last.end = Math.max(last.end, range.end);
    } else {
      merged.push({ ...range });
    }
  }

  const segments: HighlightSegment[] = [];
  let cursor = 0;
  for (const { start, end } of merged) {
    if (start > cursor) segments.push({ text: text.slice(cursor, start), match: false });
    segments.push({ text: text.slice(start, end), match: true });
    cursor = end;
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor), match: false });
  return segments;
}
